// Proton app frame (packages/components/containers/app/PrivateAppContainer.tsx,
// components/sidebar/Sidebar.tsx, containers/heading/PrivateHeader.tsx):
//
//   ┌ sidebar 250 ─────────┐┌ main (1px border-weak on the left) ─────────────┐
//   │ [logo]      [apps ⋮] ││ header 60: [search ……]    [bell] [⚙] [user ▢] │
//   │ [ primary button ]   ││ ─────────────────────────────────────────────── │
//   │ nav (route slot)     ││ route content                                   │
//   │ …                    ││                                                 │
//   │ footer (route slot)  ││                                                 │
//   └──────────────────────┘└─────────────────────────────────────────────────┘
//
// Layer7 branding uses Proton's own organisation-logo treatment
// (components/logo/CustomLogo.tsx): square mark + org name + product name.
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
  LayoutGrid, Mail, Calendar, FolderClosed, Users, Video, LifeBuoy, House, Settings as Cog,
  Search, Menu, LogOut, UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMe, useIsMobile, useLogout } from "@/hooks";
import { CmdKPalette } from "./CmdKPalette";
import { NotificationCenter } from "./NotificationCenter";
import { Toaster } from "@/components/Toaster";
import { ShellSlotsProvider, useShellSlots } from "@/proton/ui/slots";
import { Dropdown, DropdownMenu, DropdownMenuButton, DropdownSeparator } from "@/proton/ui/Dropdown";
import { Button } from "@/proton/ui/Button";
import { Icon } from "@/proton/ui/Icon";

interface AppEntry { id: string; to: string; icon: LucideIcon; label: string; gated?: string }

const APPS: AppEntry[] = [
  { id: "home",     to: "/home",     icon: House,        label: "Home" },
  { id: "mail",     to: "/mail",     icon: Mail,         label: "Mail" },
  { id: "calendar", to: "/calendar", icon: Calendar,     label: "Calendar" },
  { id: "files",    to: "/files",    icon: FolderClosed, label: "Files" },
  { id: "contacts", to: "/contacts", icon: Users,        label: "Contacts" },
  { id: "meetings", to: "/meetings", icon: Video,        label: "Meet" },
  { id: "support",  to: "/support",  icon: LifeBuoy,     label: "Support", gated: "support-agents" },
];

/** Product name under the org name, like Proton's wordmark ("Mail", "Drive"…). */
function productFor(pathname: string): string {
  const seg = "/" + (pathname.split("/").filter(Boolean)[0] ?? "");
  if (seg === "/settings") return "Account";
  if (seg === "/home") return "Workspace";
  return APPS.find((a) => a.to === seg)?.label ?? "Workspace";
}

const SEARCH_PLACEHOLDER: Record<string, string> = {
  Mail: "Search messages", Calendar: "Search events", Files: "Search files",
  Contacts: "Search contacts", Support: "Search tickets", Meet: "Search", Account: "Search settings",
};

export function initials(name?: string | null, email?: string | null): string {
  const src = (name || email || "").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function Shell() {
  return (
    <ShellSlotsProvider>
      <Frame />
    </ShellSlotsProvider>
  );
}

function Frame() {
  const me = useMe();
  const loc = useLocation();
  const isMobile = useIsMobile();
  const { register, sidebarExpanded, setSidebarExpanded } = useShellSlots();
  const groups = me.data?.groups || [];
  const apps = APPS.filter((a) => !a.gated || groups.includes(a.gated));
  const product = productFor(loc.pathname);

  // Close the mobile drawer on navigation.
  useEffect(() => { setSidebarExpanded(false); }, [loc.pathname, setSidebarExpanded]);
  // ...and on Escape.
  useEffect(() => {
    if (!isMobile || !sidebarExpanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSidebarExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, sidebarExpanded, setSidebarExpanded]);

  // Home is Proton's post-login "Welcome" page: no app sidebar or search
  // header, just the brand top-left and the account top-right over the
  // radial backdrop (applications/account/src/app/public/Layout.tsx).
  if (loc.pathname === "/home" || loc.pathname === "/") return <LandingFrame />;

  return (
    <div className="flex flex-row flex-nowrap h-full">
      <div className="flex flex-col flex-nowrap overflow-hidden flex-1 relative">
        <div className="content flex-auto flex flex-col flex-nowrap">
          <div className="flex flex-1 flex-nowrap min-h-0">
            <aside className="sidebar" data-expanded={isMobile ? sidebarExpanded : undefined} aria-label="Navigation">
              <div className="logo-container shrink-0 flex justify-between items-center flex-nowrap gap-0.5">
                <Link to="/home" className="sidebar-custom-logo items-center" title="Layer7 Workspace">
                  <span className="sidebar-custom-logo-image">
                    <img src="/brand/logo-mark.svg" alt="" className="w-full h-full object-contain p-0.5" draggable={false} />
                  </span>
                  <span className="flex flex-col justify-center min-w-0 mb-0.5">
                    <span className="sidebar-custom-logo-name text-ellipsis">Layer7</span>
                    <span className="sidebar-custom-logo-product">{product}</span>
                  </span>
                </Link>
                <AppsDropdown apps={apps} current={loc.pathname} />
              </div>
              {/* Small screens have no header user menu; it sits under the logo
                  (the logo row already carries the app switcher). */}
              {isMobile && (
                <div className="px-3 pb-3 shrink-0 flex items-center">
                  <div className="flex-1 min-w-0"><UserDropdown /></div>
                </div>
              )}
              <div className="px-3 pb-2 shrink-0" ref={register("primary")} />
              <div className="flex-1 flex flex-col flex-nowrap pb-2 md:mt-2 overflow-y-auto min-h-0" ref={register("nav")} />
              <div className="shrink-0 app-infos px-3 mt-2 pb-2" ref={register("footer")} />
            </aside>
            {isMobile && sidebarExpanded && <div className="sidebar-backdrop" onClick={() => setSidebarExpanded(false)} />}

            <div className="flex flex-col flex-1 flex-nowrap min-w-0">
              <div className="main flex flex-col flex-nowrap flex-1 min-h-0">
                <header className="header">
                  {isMobile && (
                    <Button shape="ghost" color="weak" icon aria-label="Open navigation" onClick={() => setSidebarExpanded(true)}>
                      <Icon as={Menu} />
                    </Button>
                  )}
                  <div className="flex-1 flex items-center min-w-0" ref={register("search")}>
                    <SearchBox placeholder={SEARCH_PLACEHOLDER[product] ?? "Search"} compact={isMobile} />
                  </div>
                  <ul className="flex items-center gap-2 m-0 p-0 list-none shrink-0">
                    <li className="flex" ref={register("headerActions")} />
                    <li className="flex"><NotificationCenter /></li>
                    <li className="hidden md:flex">
                      <Link to="/settings" className="button button-ghost-weak button-medium button-for-icon" aria-label="Settings" title="Settings">
                        <Icon as={Cog} />
                      </Link>
                    </li>
                    {!isMobile && <li className="relative flex"><UserDropdown /></li>}
                  </ul>
                </header>
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CmdKPalette />
      <Toaster />
    </div>
  );
}

/** Header search (_structure.scss .searchbox). Opens the global command palette. */
function SearchBox({ placeholder, compact }: { placeholder: string; compact?: boolean }) {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  const open = () => window.dispatchEvent(new CustomEvent("__cmdk_open"));
  if (compact) {
    return (
      <Button shape="ghost" color="weak" icon aria-label={placeholder} onClick={open} className="ml-auto">
        <Icon as={Search} />
      </Button>
    );
  }
  return (
    <div className="searchbox">
      <button type="button" className="searchbox-field w-full text-left" onClick={open} aria-label={placeholder}>
        <Icon as={Search} className="color-weak" />
        <span className="flex-1 text-ellipsis color-weak">{placeholder}</span>
        <kbd className="text-sm color-hint">{isMac ? "⌘K" : "Ctrl K"}</kbd>
      </button>
    </div>
  );
}

/** App switcher (containers/app/AppsDropdown.tsx + ProductIcon.tsx). */
function AppsDropdown({ apps, current }: { apps: AppEntry[]; current: string }) {
  const navigate = useNavigate();
  const seg = "/" + (current.split("/").filter(Boolean)[0] ?? "");
  return (
    <Dropdown
      className="rounded-lg"
      maxWidth="30em"
      placement="bottom-start"
      trigger={({ ref, open, toggle }) => (
        <button ref={ref} type="button" className="apps-dropdown-button shrink-0" aria-expanded={open} aria-label="Layer7 applications" title="Layer7 applications" onClick={toggle}>
          <Icon as={LayoutGrid} size={6} />
        </button>
      )}
    >
      {(close) => (
        <ul className="apps-dropdown-grid" style={{ ["--apps-dropdown-repeat" as string]: 3 }}>
          {apps.map((a) => {
            const isCurrent = a.to === seg;
            return (
              <li key={a.id}>
                <button type="button" className="apps-dropdown-link" aria-current={isCurrent ? "page" : undefined}
                  onClick={() => { close(); navigate(a.to); }}>
                  <span className="apps-dropdown-logo-wrapper"><Icon as={a.icon} size={6} /></span>
                  <span className="apps-dropdown-app-name">{a.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Dropdown>
  );
}

/** User menu (containers/heading/UserDropdownButton.tsx + UserDropdownContent). */
function UserDropdown() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const name = me.data?.name || "";
  const email = me.data?.email || "";
  const mono = initials(name, email);
  return (
    <Dropdown
      placement="bottom-end"
      minWidth="16em"
      maxWidth="22em"
      trigger={({ ref, open, toggle }) => (
        <button ref={ref} type="button" className="user-dropdown-button" aria-expanded={open} onClick={toggle}
          title={[name, `<${email}>`].filter(Boolean).join(" ")}>
          <span className="user-dropdown-text">
            <span className="block text-ellipsis text-sm">{name || email}</span>
            {name && email && <span className="block text-ellipsis color-weak text-sm m-0 lh-rg">{email}</span>}
          </span>
          <span className="user-initials text-sm my-auto" aria-hidden="true"><span className="m-auto">{mono}</span></span>
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="px-4 pt-4 pb-3 flex items-center gap-3">
            <span className="user-initials text-rg" style={{ minInlineSize: "2.5rem", minBlockSize: "2.5rem" }}>{mono}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-ellipsis text-semibold">{name || email}</span>
              {name && <span className="block text-ellipsis color-weak text-sm">{email}</span>}
            </span>
          </div>
          <DropdownMenu>
            <DropdownSeparator />
            <DropdownMenuButton icon={<Icon as={UserRound} />} onClick={() => { close(); navigate("/settings"); }}>Account settings</DropdownMenuButton>
            <DropdownMenuButton icon={<Icon as={Cog} />} onClick={() => { close(); navigate("/settings/notifications"); }}>Notifications</DropdownMenuButton>
            <DropdownSeparator />
            <DropdownMenuButton icon={<Icon as={LogOut} />} onClick={() => { close(); logout.mutate(); }}>Sign out</DropdownMenuButton>
          </DropdownMenu>
        </div>
      )}
    </Dropdown>
  );
}

function LandingFrame() {
  return (
    <div className="welcome-bg h-full overflow-y-auto flex flex-col">
      <header className="welcome-header gap-2 sm:gap-4 px-4 sm:px-6 py-3 lg:px-12 md:pt-5 md:pb-10 mb-2 md:mb-0 shrink-0">
        <Link to="/home" className="inline-flex shrink-0" aria-label="Layer7 Workspace">
          <img src="/brand/logo-horizontal.svg" alt="Layer7" className="h-7 w-auto select-none" draggable={false} />
        </Link>
        <PublicUserItem />
      </header>
      <main className="flex flex-col flex-nowrap flex-1 min-h-0">
        <Outlet />
      </main>
      <CmdKPalette />
      <Toaster />
    </div>
  );
}

/** Account chip on the Welcome page (public/PublicUserItem.tsx). */
function PublicUserItem() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const name = me.data?.name || me.data?.email || "";
  const email = me.data?.email || "";
  return (
    <Dropdown
      placement="bottom-end"
      minWidth="14em"
      trigger={({ ref, open, toggle }) => (
        <button ref={ref} type="button" className="public-user-item-button" aria-expanded={open} onClick={toggle} title="Account">
          <span className="mr-3 flex-1 min-w-0 -mt-1 text-right">
            <span className="block text-ellipsis text-bold">{name}</span>
            {email && email !== name && <span className="block text-ellipsis color-weak">{email}</span>}
          </span>
          <span className="public-user-item-initials"><span className="m-auto text-semibold" aria-hidden="true">{initials(me.data?.name, email)}</span></span>
        </button>
      )}
    >
      {(close) => (
        <DropdownMenu>
          <DropdownMenuButton icon={<Icon as={Cog} />} onClick={() => { close(); navigate("/settings"); }}>Account settings</DropdownMenuButton>
          <DropdownMenuButton icon={<Icon as={LogOut} />} onClick={() => { close(); logout.mutate(); }}>Sign out</DropdownMenuButton>
        </DropdownMenu>
      )}
    </Dropdown>
  );
}

export { APPS };
